import axiosClient from './axiosClient';
import { TicketModel } from '../Models/ticketModel';
import projectService from './projectService';
import ticketStatusService from './ticketStatusService';
import ticketStatusHistoryService from './ticketStatusHistoryService';

export const mockSeverities = [
  { severityID: 1, severityName: 'Low' },
  { severityID: 2, severityName: 'Medium' },
  { severityID: 3, severityName: 'High' },
  { severityID: 4, severityName: 'Critical' },
];

export const mockStatuses = [
  { statusID: 1, statusName: 'Open' },
  { statusID: 2, statusName: 'In Progress' },
  { statusID: 3, statusName: 'Resolved' },
  { statusID: 4, statusName: 'Closed' },
];

export const mockEnvironments = [
  { environmentID: 1, environmentName: 'Development' },
  { environmentID: 2, environmentName: 'Staging' },
  { environmentID: 3, environmentName: 'Production' },
];

const ticketService = {
  // ---------- Ticket_TBL ----------
  getAll: (params = {}) => {
    // Map query parameter casings
    const requestParams = {};
    if (params.projectID || params.projectId) requestParams.projectId = Number(params.projectID || params.projectId);
    if (params.statusID || params.statusId) requestParams.statusId = Number(params.statusID || params.statusId);
    if (params.severityID || params.severityId) requestParams.severityId = Number(params.severityID || params.severityId);
    if (params.search) requestParams.search = params.search;

    return axiosClient.get('/Ticket', { params: requestParams })
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("GET /Ticket returned 404. Falling back to GET /tickets...");
          return axiosClient.get('/tickets', { params: requestParams });
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(TicketModel.toFrontend);
        return {
          data: mappedList,
        };
      });
  },

  getById: (ticketID) => {
    return axiosClient.get(`/Ticket/${ticketID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`GET /Ticket/${ticketID} returned 404. Falling back to GET /tickets/${ticketID}...`);
          return axiosClient.get(`/tickets/${ticketID}`);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  create: async (payload) => {
    // Determine the clientOrgID from the selected project
    let clientOrgID = payload.clientOrgID;
    if (!clientOrgID && payload.projectID) {
      try {
        const projRes = await projectService.getById(payload.projectID);
        if (projRes?.data) {
          clientOrgID = projRes.data.clientOrgID;
        }
      } catch (e) {
        console.error("Failed to fetch project for clientOrgID:", e);
      }
    }

    // Determine the first/default statusId configured for the selected project
    let statusID = payload.statusID || payload.statusId;
    if (payload.projectID) {
      try {
        const statusRes = await ticketStatusService.getAll();
        if (statusRes?.data) {
          const projStats = statusRes.data.filter(s => Number(s.projectID) === Number(payload.projectID));
          if (projStats.length > 0) {
            // Sort by sequenceOrder to find the first one (lowest sequence)
            const sorted = [...projStats].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
            statusID = sorted[0].statusID;
          }
        }
      } catch (e) {
        console.error("Failed to fetch default status for project:", e);
      }
    }

    // Get logged-in user ID
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {
      console.error("Failed to parse user from local storage:", e);
    }

    const body = TicketModel.toBackend({
      ...payload,
      clientOrgID: clientOrgID,
      statusID: statusID || payload.statusID,
      raisedBy: loggedInUserId,
      lastUpdatedBy: loggedInUserId,
    });
    body.ticketId = 0;
    body.ticketNumber = !body.ticketNumber || body.ticketNumber === 'string'
      ? ("TCK-" + Math.floor(100000 + Math.random() * 900000))
      : body.ticketNumber;

    return axiosClient.post('/Ticket', body)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("POST /Ticket returned 404. Falling back to POST /tickets...");
          return axiosClient.post('/tickets', body);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  update: async (ticketID, payload) => {
    // Get logged-in user ID
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {
      console.error("Failed to parse user from local storage:", e);
    }

    let mergedPayload = { ...payload };
    if (!payload.projectID && !payload.projectId) {
      try {
        const existing = await ticketService.getById(ticketID);
        if (existing?.data) {
          mergedPayload = {
            ...existing.data,
            ...payload,
          };
        }
      } catch (err) {
        console.warn("Could not retrieve ticket before update:", err);
      }
    }

    // Determine the clientOrgID from the selected project (if not present)
    let clientOrgID = mergedPayload.clientOrgID || mergedPayload.clientOrgId;
    if (!clientOrgID && (mergedPayload.projectID || mergedPayload.projectId)) {
      try {
        const projRes = await projectService.getById(mergedPayload.projectID || mergedPayload.projectId);
        if (projRes?.data) {
          clientOrgID = projRes.data.clientOrgID || projRes.data.clientOrgId;
        }
      } catch (e) {
        console.error("Failed to fetch project for clientOrgID:", e);
      }
    }

    const body = TicketModel.toBackend({
      ...mergedPayload,
      ticketID,
      clientOrgID: clientOrgID || mergedPayload.clientOrgID,
      lastUpdatedBy: loggedInUserId,
    });

    return axiosClient.put('/Ticket', body)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("PUT /Ticket returned 404. Falling back to PUT /tickets...");
          return axiosClient.put('/tickets', body);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return {
          data: TicketModel.toFrontend(raw),
        };
      });
  },

  remove: (ticketID) => {
    return axiosClient.delete(`/Ticket/${ticketID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`DELETE /Ticket/${ticketID} returned 404. Falling back to DELETE /tickets/${ticketID}...`);
          return axiosClient.delete(`/tickets/${ticketID}`);
        }
        return Promise.reject(err);
      });
  },

  changeStatus: async (ticketID, payload) => {
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {}

    // 1. Record in history
    await ticketStatusHistoryService.create({
      ticketID: Number(ticketID),
      fromStatusID: payload.fromStatusID,
      toStatusID: payload.toStatusID,
      changedBy: loggedInUserId,
    }).catch(() => {});

    // 2. Update ticket status in database with full payload
    return ticketService.update(ticketID, {
      ...payload,
      statusID: payload.toStatusID,
      reopenReasonID: payload.reopenReasonID,
    });
  },

  // Connect sub-resources to flat backend controllers
  getHistory: (ticketID) => {
    return ticketStatusHistoryService.getAll({ ticketID });
  },

  getAssignments: (ticketID) => {
    return axiosClient.get('/TicketAssignment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/assignments`))
      .then(res => {
        let list = res?.data?.data || res?.data || [];
        if (!Array.isArray(list)) {
          list = list ? [list] : [];
        }
        const filtered = list.filter(a => a && Number(a.ticketId || a.ticketID) === Number(ticketID))
          .map(a => ({
            assignmentID: a.assignmentId || a.assignmentID,
            ticketID: a.ticketId || a.ticketID,
            userID: a.userId || a.userID,
            assignedBy: a.assignedBy,
            assignedDttm: a.assignedDttm,
            unassignedDttm: a.unassignedDttm,
            isCurrentFlag: a.isCurrentFlag,
            userName: a.userName || `User ${a.userId || a.userID}`,
          }));
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  assign: (ticketID, userID) => {
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {}

    const body = {
      assignmentId: 0,
      ticketId: Number(ticketID),
      userId: Number(userID),
      assignedBy: loggedInUserId,
      assignedDttm: new Date().toISOString(),
      unassignedDttm: null,
      isCurrentFlag: true,
    };

    return axiosClient.post('/TicketAssignment', body);
  },

  unassign: (ticketID, assignmentID) => {
    return axiosClient.delete(`/TicketAssignment/${assignmentID}`);
  },

  getComments: (ticketID) => {
    return axiosClient.get('/TicketComment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/comments`))
      .then(res => {
        let list = res?.data?.data || res?.data || [];
        if (!Array.isArray(list)) {
          list = list ? [list] : [];
        }
        const filtered = list.filter(c => c && Number(c.ticketId || c.ticketID) === Number(ticketID))
          .map(c => ({
            commentID: c.commentId || c.commentID,
            ticketID: c.ticketId || c.ticketID,
            commentedBy: c.commentedBy,
            commentText: c.commentText,
            isInternalFlag: c.isInternalFlag,
            createdDttm: c.createdDttm || c.createDttm,
            commentedByName: c.commentedByName || `User ${c.commentedBy}`,
          }));
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  addComment: (ticketID, payload) => {
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {}

    const body = {
      commentId: 0,
      ticketId: Number(ticketID),
      commentedBy: loggedInUserId,
      commentText: payload.commentText,
      isInternalFlag: !!payload.isInternalFlag,
      createdDttm: new Date().toISOString(),
    };

    return axiosClient.post('/TicketComment', body);
  },

  getAttachments: (ticketID) => {
    return axiosClient.get('/TicketAttachment')
      .catch(() => axiosClient.get(`/tickets/${ticketID}/attachments`))
      .then(res => {
        const list = res?.data?.data || res?.data || [];
        const filtered = list.filter(a => Number(a.ticketId || a.ticketID) === Number(ticketID))
          .map(a => ({
            attachmentID: a.attachmentId || a.attachmentID,
            ticketID: a.ticketId || a.ticketID,
            fileName: a.fileName,
            filePath: a.filePath,
            fileSizeKB: a.fileSizeKB,
            fileType: a.fileType,
            virusScanStatus: a.virusScanStatus,
            uploadedBy: a.uploadedBy,
            uploadedDttm: a.uploadedDttm,
          }));
        return { data: filtered };
      })
      .catch(() => ({ data: [] }));
  },

  uploadAttachment: (ticketID, files) => {
    const fileList = Array.isArray(files) ? files : [files];
    const formData = new FormData();
    formData.append('AttachmentId', '0');
    formData.append('TicketId', String(ticketID));
    
    fileList.forEach(file => {
      formData.append('Files', file);
    });

    if (fileList.length > 0) {
      formData.append('FileName', fileList[0].name || 'attachment');
      formData.append('FilePath', fileList[0].name || 'temp'); // satisfy validation
      formData.append('FileSizeKB', String(Math.round(fileList[0].size / 1024)));
      formData.append('FileType', fileList[0].type || 'application/octet-stream');
    } else {
      formData.append('FileName', 'attachment');
      formData.append('FilePath', 'temp');
      formData.append('FileSizeKB', '0');
      formData.append('FileType', 'application/octet-stream');
    }

    formData.append('VirusScanStatus', 'Pending');
    
    let loggedInUserId = 1;
    try {
      const userStr = localStorage.getItem('afix_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        loggedInUserId = Number(user.userID || user.userId || 1);
      }
    } catch (e) {}
    formData.append('UploadedBy', String(loggedInUserId));
    formData.append('UploadedDttm', new Date().toISOString());

    return axiosClient.post('/TicketAttachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  removeAttachment: (ticketID, attachmentID) => {
    return axiosClient.delete(`/TicketAttachment/${attachmentID}`);
  },

  getLinks: (ticketID) => Promise.resolve({ data: [] }),
  addLink: (ticketID, payload) => Promise.resolve({ data: payload }),
  getClientAnswers: (ticketID) => Promise.resolve({ data: [] }),
  submitClientAnswers: (ticketID, answers) => Promise.resolve({ data: { success: true } }),
};

export default ticketService;
